import { z } from 'zod';

// ─── Opportunity Event Payloads ─────────────────────────────────────
// Zod schemas for every opportunity-domain event.
// Consumers validate incoming payloads against these at the boundary.

// Opportunity.id is an autoincrement Int, serialized as a numeric string.
const OpportunityIdSchema = z.string().regex(/^\d+$/, 'Invalid opportunity id');

export const OpportunityGenerateRequestedPayload = z.object({
    tenantId: z.string().cuid2(),
    /** Owning set — task-progress navigation; optional for in-flight messages predating AD-2262. */
    setId: z.string().min(1).optional(),
    requestedBy: z.string().cuid(),
    opportunityIds: z.array(OpportunityIdSchema).min(1),
    action: z.enum(['scripts']),
    filters: z.record(z.string(), z.unknown()).optional(),
});
export type OpportunityGenerateRequestedPayload = z.infer<typeof OpportunityGenerateRequestedPayload>;

export const OpportunityGeneratedPayload = z.object({
    tenantId: z.string().cuid2(),
    opportunityId: z.string().cuid(),
    title: z.string().min(1),
    generatedBy: z.string().cuid(),
});
export type OpportunityGeneratedPayload = z.infer<typeof OpportunityGeneratedPayload>;

export const OpportunityScoredPayload = z.object({
    tenantId: z.string().cuid2(),
    opportunityId: z.string().cuid(),
    score: z.number().min(0).max(100),
    scoredBy: z.string().min(1),
});
export type OpportunityScoredPayload = z.infer<typeof OpportunityScoredPayload>;

export const OpportunityApprovedPayload = z.object({
    tenantId: z.string().cuid2(),
    opportunityId: z.string().cuid(),
    approvedBy: z.string().cuid(),
});
export type OpportunityApprovedPayload = z.infer<typeof OpportunityApprovedPayload>;

export const OpportunityRejectedPayload = z.object({
    tenantId: z.string().cuid2(),
    opportunityId: z.string().cuid(),
    rejectedBy: z.string().cuid(),
    reason: z.string().min(1),
});
export type OpportunityRejectedPayload = z.infer<typeof OpportunityRejectedPayload>;

// ─── Export Payload ─────────────────────────────────────────────────

const ExportModeSelectedPayload = z.object({
    mode: z.literal('selected'),
    opportunityIds: z.array(z.string().regex(/^\d+$/, 'Opportunity ID must be numeric')).min(1),
});

const ExportModeFilteredPayload = z.object({
    mode: z.literal('filtered'),
    tab: z.string().optional(),
    search: z.string().optional(),
    sortBy: z.string().optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
    action: z.string().optional(),
    flowName: z.string().optional(),
    feature: z.string().optional(),
    subFeature: z.string().optional(),
    engine: z.string().optional(),
});

const ExportModeAllPayload = z.object({
    mode: z.literal('all'),
});

const ExportModeUnion = z.discriminatedUnion('mode', [
    ExportModeSelectedPayload,
    ExportModeFilteredPayload,
    ExportModeAllPayload,
]);

export const OpportunityExportRequestedPayload = z.object({
    tenantId: z.string().cuid2(),
    requestedBy: z.string().cuid(),
    setId: z.string().min(1),
}).and(ExportModeUnion);
export type OpportunityExportRequestedPayload = z.infer<typeof OpportunityExportRequestedPayload>;

// ─── Bulk Action Payloads ───────────────────────────────────────────

// ─── Generate New Set Payload ───────────────────────────────────────

export const OpportunityGenerateNewSetRequestedPayload = z.object({
    tenantId: z.string().min(1),
    setId: z.string().min(1),
    requestedBy: z.string().optional(),
    products: z.array(z.string()).optional(),
    my_brand: z.string().optional(),
    main_competitor: z.string().optional(),
    industry_competitors: z.array(z.string()).optional(),
    // ─── Legacy fields (deprecated — engine reads from DB + tenant settings) ───
    platform: z.string().optional(),
    premise: z.string().optional(),
    user_name: z.string().optional(),
    source: z.string().optional(),
    industry: z.string().optional(),
    trigger_type: z.string().optional(),
    user_type: z.string().optional(),
});
export type OpportunityGenerateNewSetRequestedPayload = z.infer<typeof OpportunityGenerateNewSetRequestedPayload>;

export const BulkRejectRequestedPayload = z.object({
    tenantId: z.string().cuid2(),
    requestedBy: z.string().cuid(),
    opportunityIds: z.array(OpportunityIdSchema).min(1),
    reason: z.string().min(1),
});
export type BulkRejectRequestedPayload = z.infer<typeof BulkRejectRequestedPayload>;

// ─── AI Edit Script Payload ─────────────────────────────────────────

/** Shared with the AI Edit textarea so the client stops input at the same cap the API enforces. */
export const AI_EDIT_INSTRUCTION_MAX_LENGTH = 2000;

export const AiEditScriptRequestedPayload = z.object({
    tenantId: z.string().min(1),
    requestedBy: z.string().min(1),
    setId: z.string().min(1),
    scriptIds: z.array(z.string().min(1)).min(1),
    instruction: z.string().min(1).max(AI_EDIT_INSTRUCTION_MAX_LENGTH),
});
export type AiEditScriptRequestedPayload = z.infer<typeof AiEditScriptRequestedPayload>;
